import { useEffect, useRef } from 'react';
import { useGameStore } from '../stores/gameStore';

export const useBounceLoop = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
  const loopRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);

  const { bounceTick, bounce } = useGameStore();

  useEffect(() => {
    const step = (time: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const dt = Math.min((time - lastTimeRef.current) / 1000, 0.05);
      lastTimeRef.current = time;

      if (bounce.phase === 'playing') {
        bounceTick(dt);
      }

      const gameWidth = 160;
      const gameHeight = 120;

      const width = canvas.width;
      const height = canvas.height;
      const scaleX = width / gameWidth;
      const scaleY = height / gameHeight;

      ctx.setTransform(scaleX, 0, 0, scaleY, 0, 0);
      ctx.clearRect(0, 0, gameWidth, gameHeight);

      // LCD background
      ctx.fillStyle = '#001100';
      ctx.fillRect(0, 0, gameWidth, gameHeight);

      // Grid lines
      ctx.strokeStyle = 'rgba(0, 255, 0, 0.1)';
      ctx.lineWidth = 1 / Math.max(scaleX, scaleY);
      for (let y = 0; y < gameHeight; y += 8) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(gameWidth, y);
        ctx.stroke();
      }

      // Platforms
      ctx.fillStyle = '#00aa00';
      bounce.platforms.forEach((p) => {
        ctx.fillRect(p.x, p.y, p.width, p.height);
      });

      // Spikes
      ctx.fillStyle = '#00ff00';
      bounce.spikes.forEach((s) => {
        ctx.beginPath();
        const spikesCount = Math.max(3, Math.floor(s.width / 10));
        const spikeWidth = s.width / spikesCount;
        for (let i = 0; i < spikesCount; i++) {
          const sx = s.x + i * spikeWidth;
          ctx.moveTo(sx, s.y + s.height);
          ctx.lineTo(sx + spikeWidth / 2, s.y);
          ctx.lineTo(sx + spikeWidth, s.y + s.height);
        }
        ctx.fill();
      });

      // Ball
      ctx.fillStyle = '#00ff66';
      ctx.beginPath();
      ctx.arc(bounce.ballX, bounce.ballY, bounce.radius, 0, Math.PI * 2);
      ctx.fill();

      // HUD
      ctx.fillStyle = '#00ff66';
      ctx.font = "8px 'Courier New', monospace";
      ctx.textBaseline = 'top';
      ctx.fillText(
        `SCORE: ${bounce.score.toString().padStart(4, '0')}`,
        2,
        2
      );
      ctx.fillText(`LVL: ${bounce.level}`, gameWidth - 40, 2);

      loopRef.current = requestAnimationFrame(step);
    };

    lastTimeRef.current = performance.now();
    loopRef.current = requestAnimationFrame(step);

    return () => {
      if (loopRef.current) cancelAnimationFrame(loopRef.current);
    };
  }, [bounce, bounceTick, canvasRef]);
};


